// Page d'import CSV de produits
// Permet d'importer massivement des produits depuis un fichier CSV

import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, FileText, CheckCircle2, AlertCircle, Download,
  Package, Loader2, ArrowLeft, Info, X, Eye, ShoppingBag
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

export default function ImportCSVPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [publishToMarketplace, setPublishToMarketplace] = useState(true);
  const [result, setResult] = useState(null);
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      setFile(droppedFile);
      setResult(null);
    } else {
      toast.error('Seuls les fichiers CSV sont acceptés');
    }
  };
  
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
    }
  };
  
  const handleUpload = async () => {
    if (!file) return;
    
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Tu dois être connecté');
      return;
    }
    
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(
        `${API_URL}/api/cockpit/products/import-csv?publish_to_marketplace=${publishToMarketplace}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'import');
      }
      
      setResult(data);
      
      if (data.imported_count > 0) {
        toast.success(`${data.imported_count} produit(s) importé(s) avec succès !`);
      }
      
      if (data.error_count > 0) {
        toast.warning(`${data.error_count} erreur(s) détectée(s)`);
      }
      
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.message || 'Erreur lors de l\'import');
    } finally {
      setIsUploading(false);
    }
  };
  
  const downloadTemplate = () => {
    const csvContent = `nom;description;prix;stock;categorie;marque;reference;image_url
"Perceuse sans fil 18V";"Perceuse professionnelle avec batterie lithium";"149.99";"25";"Outillage";"Bosch";"PERC-18V-001";"https://example.com/image.jpg"
"Lot de 10 tournevis";"Kit complet de tournevis professionnels";"34.90";"50";"Outillage";"Stanley";"TOUR-KIT-010";""
"Peinture blanche 10L";"Peinture acrylique mate pour intérieur";"45.00";"100";"Peinture";"Dulux";"PEIN-BLC-10L";""`;
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'modele_import_produits.csv';
    link.click();
  };
  
  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Import CSV</h1>
            <p className="text-sm text-gray-500">Importe tes produits en masse</p>
          </div>
        </div>
      </div>
      
      <div className="p-4 space-y-6 max-w-2xl mx-auto">
        {/* Instructions */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 border border-blue-200 rounded-xl p-4"
        >
          <div className="flex gap-3">
            <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-2">Format du fichier CSV :</p>
              <ul className="space-y-1 text-blue-700">
                <li>• Colonnes : <strong>nom, description, prix, stock, categorie, marque, reference, image_url</strong></li>
                <li>• Séparateur : point-virgule (;) ou virgule (,)</li>
                <li>• Encodage : UTF-8 recommandé</li>
                <li>• Prix : format numérique (ex: 149.99)</li>
              </ul>
              <button 
                onClick={downloadTemplate}
                className="mt-3 flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
              >
                <Download size={16} />
                Télécharger le modèle CSV
              </button>
            </div>
          </div>
        </motion.div>
        
        {/* Upload Zone */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`
              relative border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all
              ${isDragging 
                ? 'border-[#1E3A5F] bg-[#1E3A5F]/5' 
                : file 
                  ? 'border-green-400 bg-green-50' 
                  : 'border-gray-300 hover:border-gray-400 bg-white'
              }
            `}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {file ? (
              <div className="space-y-3">
                <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                  <FileText size={32} className="text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{file.name}</p>
                  <p className="text-sm text-gray-500">
                    {(file.size / 1024).toFixed(1)} Ko
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                    setResult(null);
                  }}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Supprimer
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                  <Upload size={32} className="text-gray-400" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    Glisse ton fichier CSV ici
                  </p>
                  <p className="text-sm text-gray-500">
                    ou clique pour sélectionner
                  </p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
        
        {/* Options */}
        {file && !result && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-4 border border-gray-200"
          >
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-3">
                <ShoppingBag size={20} className="text-[#1E3A5F]" />
                <div>
                  <p className="font-medium text-gray-900">Publier sur la Marketplace</p>
                  <p className="text-sm text-gray-500">Les produits seront visibles par tous</p>
                </div>
              </div>
              <div 
                className={`w-12 h-6 rounded-full transition-colors relative ${
                  publishToMarketplace ? 'bg-[#1E3A5F]' : 'bg-gray-300'
                }`}
                onClick={() => setPublishToMarketplace(!publishToMarketplace)}
              >
                <div 
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    publishToMarketplace ? 'left-7' : 'left-1'
                  }`} 
                />
              </div>
            </label>
          </motion.div>
        )}
        
        {/* Upload Button */}
        {file && !result && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Button
              onClick={handleUpload}
              disabled={isUploading}
              className="w-full py-6 text-lg bg-[#1E3A5F] hover:bg-[#2D4A6F]"
            >
              {isUploading ? (
                <>
                  <Loader2 size={20} className="animate-spin mr-2" />
                  Import en cours...
                </>
              ) : (
                <>
                  <Upload size={20} className="mr-2" />
                  Importer les produits
                </>
              )}
            </Button>
          </motion.div>
        )}
        
        {/* Result */}
        <AnimatePresence>
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              {/* Summary */}
              <div className={`rounded-xl p-6 ${
                result.error_count === 0 ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'
              }`}>
                <div className="flex items-center gap-3 mb-4">
                  {result.error_count === 0 ? (
                    <CheckCircle2 size={28} className="text-green-600" />
                  ) : (
                    <AlertCircle size={28} className="text-amber-600" />
                  )}
                  <h3 className="text-lg font-bold text-gray-900">
                    Import terminé
                  </h3>
                </div>
                
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{result.total_rows}</p>
                    <p className="text-sm text-gray-600">Lignes lues</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{result.imported_count}</p>
                    <p className="text-sm text-gray-600">Importés</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">{result.error_count}</p>
                    <p className="text-sm text-gray-600">Erreurs</p>
                  </div>
                </div>
                
                {result.published_to_marketplace && result.imported_count > 0 && (
                  <div className="mt-4 pt-4 border-t border-green-200 flex items-center gap-2 text-green-700">
                    <ShoppingBag size={16} />
                    <span className="text-sm">Produits publiés sur la Marketplace</span>
                  </div>
                )}
              </div>
              
              {/* Imported Products Preview */}
              {result.imported_products && result.imported_products.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <h4 className="font-semibold text-gray-900">Produits importés</h4>
                  </div>
                  <div className="divide-y divide-gray-100 max-h-60 overflow-y-auto">
                    {result.imported_products.map((product, idx) => (
                      <div key={idx} className="px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Package size={18} className="text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">{product.name}</span>
                        </div>
                        <span className="text-sm font-semibold text-[#1E3A5F]">
                          {product.price?.toFixed(2)} €
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Errors */}
              {result.errors && result.errors.length > 0 && (
                <div className="bg-red-50 rounded-xl border border-red-200 overflow-hidden">
                  <div className="px-4 py-3 bg-red-100 border-b border-red-200">
                    <h4 className="font-semibold text-red-800">Erreurs détectées</h4>
                  </div>
                  <div className="divide-y divide-red-100 max-h-40 overflow-y-auto">
                    {result.errors.map((error, idx) => (
                      <div key={idx} className="px-4 py-2 text-sm">
                        <span className="font-medium text-red-700">Ligne {error.row}:</span>{' '}
                        <span className="text-red-600">{error.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setFile(null);
                    setResult(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Nouvel import
                </Button>
                <Button
                  onClick={() => navigate('/cockpit-pro/catalogue/produits')}
                  className="flex-1 bg-[#1E3A5F] hover:bg-[#2D4A6F]"
                >
                  <Eye size={18} className="mr-2" />
                  Voir mes produits
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
